import * as React from 'react';

/* ResizeHandle — kit primitive: a vertical separator that resizes a side pane.
   Hand-built (alternative: react-resizable-panels — not added, no new
   dependencies). Pointer drag reports the CUMULATIVE horizontal travel since
   the grab, so the owner computes from the width it measured at drag start.
   Keyboard: ArrowLeft / ArrowRight nudge by `step` (rem), Home / End jump to
   the min / max. The hit area is --space-2 wide (Fitts); the visible line is
   1px and takes the primary colour on hover and drag; keyboard focus paints the
   universal :focus-visible ring from base.css (no inline outline override). The
   owner positions it (absolute on the pane edge, or a flex item). */

export interface ResizeHandleProps {
  'aria-label': string;
  /** Current / min / max pane size, in the owner's unit (a percent of the
   *  workspace is typical) — exposed to assistive tech, never used to size. */
  valueNow: number;
  valueMin: number;
  valueMax: number;
  /** Keyboard nudge in rem (default 1). */
  step?: number;
  /** Pointer grabbed the handle. */
  onResizeStart?: () => void;
  /** Cumulative pointer travel in px since the grab (negative = leftwards). */
  onResize: (dxPx: number) => void;
  /** Pointer released. */
  onResizeEnd?: () => void;
  /** Arrow key: signed px (ArrowLeft negative). */
  onNudge: (dxPx: number) => void;
  /** Home / End. */
  onJump: (to: 'min' | 'max') => void;
  style?: React.CSSProperties;
}

const rootRemPx = () => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

export function ResizeHandle({
  'aria-label': ariaLabel,
  valueNow,
  valueMin,
  valueMax,
  step = 1,
  onResizeStart,
  onResize,
  onResizeEnd,
  onNudge,
  onJump,
  style,
}: ResizeHandleProps) {
  const [hover, setHover] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const startX = React.useRef(0);
  const active = hover || dragging;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    startX.current = e.clientX;
    setDragging(true);
    onResizeStart?.();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging) onResize(e.clientX - startX.current);
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setDragging(false);
    onResizeEnd?.();
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') onNudge(-step * rootRemPx());
    else if (e.key === 'ArrowRight') onNudge(step * rootRemPx());
    else if (e.key === 'Home') onJump('min');
    else if (e.key === 'End') onJump('max');
    else return;
    e.preventDefault();
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={valueNow}
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      style={{
        width: 'var(--space-2)',
        display: 'flex',
        justifyContent: 'center',
        cursor: 'col-resize',
        touchAction: 'none',
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: dragging ? 2 : 1,
          height: '100%',
          background: active ? 'var(--color-primary)' : 'var(--color-border)',
          transition: 'background-color var(--duration-fast) var(--ease-out)',
        }}
      />
    </div>
  );
}

export default ResizeHandle;
